<template>
  <div>
    <div class="row">
      <div class="col-12 col-md-8">
        <div class="form-group no-gutters">
          <div v-if="edit && !is_orphaned">
            <div class="row align-items-center">
              <div class="col-12 my-2">
                <strong v-if="storage_switcher_active" class="font-weight-bold mr-2">Current Storage Asset:</strong>
                <a :href="`/storage/${item.storage.uuid}/edit`">{{ item.storage.name }}</a>
              </div>
              <div class="col-12 col-md-8">
                <!-- PLAYER SHOWN IF NOT EDITING: -->
                <audio v-if="!storage_switcher_active" id="item-storage-audio"
                       controls
                       class="w-100">
                  <source :src="storage_file_url" type="audio/mpeg">
                  Your browser does not support the audio element.
                </audio>
                <!-- OTHERWISE, SHOW EDIT CONTROLS: -->
                <div v-else class="mt-3 p-4 border rounded">
                  <item-storage-select v-model="new_storage_asset" />
                  <div class="col-12 p-0 mt-3">
                    <button v-if="new_storage_asset" class="btn btn-ss-standout" @click="() => confirmStorageAssetChange(new_storage_asset)">
                      <i class="la la-save" /> Confirm
                    </button>
                    <button class="btn btn-ss" @click="storage_switcher_active = false; new_storage_asset = null">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-4">
                <button v-if="!storage_switcher_active" class="btn btn-ss" @click="storage_switcher_active = true">
                  <i class="la la-edit" /> Change Storage Asset
                </button>
              </div>
            </div>
          </div>

          <div v-if="is_orphaned">
            <div class="alert alert-ss alert-vertical fade show" role="alert">
              <div class="alert-icon">
                <i class="flaticon-warning" />
              </div>
              <h3 class="alert-heading">
                {{ getItemLabel(true) }} is orphaned!
              </h3>
              <div class="alert-text">
                The storage asset for this {{ getItemLabel() }} has been deleted. You will need to select a new one, or delete this {{ getItemLabel(true) }}.
              </div>
            </div>
          </div>

          <label v-else-if="!loading_storage && show_storage_item_selection" for="file_selection">Storage Items</label>
          <label v-if="loading_storage" for="file_selection">Retrieving Storage Items...</label>

          <multiselect v-if="show_storage_item_selection"
                       id="file_selection"
                       v-model="selected_storage_item"
                       :loading="loading_storage"
                       :options="storage_items"
                       :searchable="true"
                       :internal-search="false"
                       :hide-selected="true"
                       placeholder="Select a Storage Item..."
                       label="name"
                       track-by="name"
                       :show-labels="false"
                       @search-change="search => assetLoader(search)">
            <template #option="props">
              <div class="kt-widget4">
                <div class="kt-widget4__item">
                  <div class="kt-widget4__img kt-widget4__img--logo">
                    <img src="" alt="">
                  </div>
                  <div class="kt-widget4__info">
                    <span class="kt-widget4__title">{{ props.option.name }}</span><br>
                  </div>
                  <span class="kt-widget4__ext">
                    <span class="kt-widget4__number">{{ formatFileSize(props.option.file_size) }}</span>
                  </span>
                </div>
              </div>
            </template>
          </multiselect>
        </div>
        <div v-if="selected_storage_item || edit">
          <!-- div v-if="edit" class="form-group kt-form__group no-gutters">
            <label for="item_uuid">{{ getItemLabel(true) }} UUID</label>
            <input id="item_uuid" type="text"
                   class="form-control kt-input" :value="item.uuid"
                   disabled>
          </div -->

          <div class="form-group kt-form__group no-gutters mt-2">
            <label for="name">Name</label> <em>*</em>
            <input id="name" v-model="name"
                   type="text"
                   class="form-control kt-input" placeholder="Enter name...">
          </div>
          <div class="form-group kt-form__group no-gutters">
            <label for="description">Description</label>
            <tip-tap v-model="description" input-id="description" />
          </div>

          <div class="form-group kt-form__group no-gutters">
            <ss-checkbox id="description_incl_chapters" v-model="description_incl_chapters">
              Include Chapters after Description
            </ss-checkbox>
          </div>

          <div class="row">
            <div class="col-6">
              <hr>
              <div class="form-group kt-form__group no-gutters row">
                <label class="col-md-4 col-form-label">Published</label>
                <div class="col-md-2">
                  <span class="kt-switch kt-switch--icon">
                    <label>
                      <input v-model="published" type="checkbox"
                             :checked="published"
                             name="">
                      <span />
                    </label>
                  </span>
                </div>
                <small>Publish schedules will override this value</small>
              </div>
            </div>

            <div class="col-6">
              <hr>
              <div class="form-group kt-form__group no-gutters row">
                <label class="col-md-4 col-form-label">Downloadable</label>
                <div class="col-md-2">
                  <span class="kt-switch kt-switch--icon">
                    <label>
                      <input v-model="downloadable" type="checkbox"
                             :checked="downloadable"
                             name="">
                      <span />
                    </label>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <div class="m-form__group form-group no-gutters">
                <ss-checkbox id="schedule-publish-dates" v-model="schedule_publish_dates">
                  Schedule Publish Dates
                </ss-checkbox>
              </div>
            </div>
            <div v-show="schedule_publish_dates" class="col-6">
              <div class="form-group kt-form__group no-gutters">
                <label for="js-publish_start">Publish From</label>
                <datetime id="js-publish_start"
                          v-model="publish_start"
                          input-class="form-control kt-input"
                          type="datetime"
                          format="yyyy-MM-dd HH:mm:ss"
                          :zone="timezone" />
              </div>
            </div>

            <div v-show="schedule_publish_dates" class="col-6">
              <div v-if="! never_unpublish" class="form-group kt-form__group no-gutters">
                <label for="js-publish_stop">Publish Until</label>
                <datetime id="js-publish_stop"
                          v-model="publish_stop"
                          input-class="form-control kt-input" type="datetime"
                          format="yyyy-MM-dd HH:mm:ss"
                          :zone="timezone" />
              </div>
              <div class="form-group kt-form__group no-gutters">
                <ss-checkbox id="never-unpublish" v-model="never_unpublish" class="mt-4">
                  Never Unpublish
                </ss-checkbox>
              </div>
            </div>
          </div>

          <div v-if="item.type === 1" class="row">
            <div class="col-12 col-md-6">
              <div class="form-group kt-form__group">
                <label for="item_broadcast_start">Broadcast Start</label>

                <datetime id="js-item_broadcast_start"
                          v-model="broadcast_start"
                          input-class="form-control kt-input"
                          type="datetime"
                          format="dd/MM/yyyy HH:mm:ss" />

                <span class="kt-form__help">Date when broadcast/recording started.</span>
                <span v-show="errors.has('Episode Link', 'details-form')" class="text-ss-danger"><br>{{ errors.first('Episode Link', 'details-form') }}</span>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="form-group kt-form__group">
                <label for="item_broadcast_stop">Broadcast Stop</label>
                <datetime id="js-item_broadcast_stop"
                          v-model="broadcast_stop"
                          input-class="form-control kt-input"
                          type="datetime"
                          format="dd/MM/yyyy HH:mm:ss" />
                <span class="kt-form__help">Date when broadcast/recording ended.</span>
              </div>
            </div>
          </div>

          <div v-if="edit" class="row">
            <div class="col-12">
              <div id="additional_info_accordion" class="accordion accordion-toggle-arrow">
                <div v-perms="['chapters']" class="card">
                  <div id="chapter_markers_accordion_heading" class="card-header">
                    <div class="card-title" data-toggle="collapse"
                         data-target="#chapter_markers_accordion_collapse"
                         aria-expanded="false" aria-controls="chapter_markers_accordion_collapse">
                      <i class="fa fa-map-marker" /> Chapter Markers
                    </div>
                  </div>
                  <div id="chapter_markers_accordion_collapse" class="collapse" data-parent="#additional_info_accordion">
                    <div class="card-body">
                      <chapters-table :item="item" />
                    </div>
                  </div>
                </div>
                <div class="card">
                  <div id="feed_items_accordion_heading" class="card-header">
                    <div class="card-title" data-toggle="collapse"
                         data-target="#feed_items_accordion_collapse"
                         aria-expanded="false" aria-controls="feed_items_accordion_collapse">
                      <i class="fa fa-rss" /> {{ getFeedLabel(true) }}s
                    </div>
                  </div>
                  <div id="feed_items_accordion_collapse" class="collapse" data-parent="#additional_info_accordion">
                    <div class="card-body">
                      <item-feeds-list :item="item" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="selected_storage_item || edit" class="col-12 col-md-3 offset-md-1">
        <display-with-selection-modal :preselected-artwork="artwork_to_display" @selected="(ev) => (selected_artwork = ev.selected_artwork)" />
        <label for="item_tags">Item Tags</label>
        <multiselect id="item_tags"
                     v-model="ms_tag_values"
                     :options="auto_complete_tags"
                     :multiple="true"
                     :taggable="true"
                     track-by="uuid"
                     label="name"
                     placeholder="Search or add new tags..."
                     @tag="addNewTag" />
      </div>
    </div>
    <confirm-modal name="confirm-storage-change"
                   confirm-icon="la la-save"
                   title="Are you sure?"
                   icon="la la-exclamation-triangle">
      <p><strong>Switching storage will result in loss of chapter markers and metadata!</strong></p>
      <p>The storage asset will be renamed to match the item name.</p>
    </confirm-modal>
  </div>
</template>
<script>
import ldDebounce from 'lodash/debounce';

import {
  formatBytes,
  mysqlDateTimeUTCToLocal,
} from 'Helpers/formatters';
import ItemStorageSelect from "VueComponents/ItemCreation/ItemStorageSelect";
import ItemFeedsList from "VuePartials/Feeds/ItemFeedsList";
import ChaptersTable from "VuePartials/App/Item/ChaptersTable";
import DisplayWithSelectionModal from "VuePartials/Artwork/DisplayWithSelectionModal";
import ItemPublishSettingsMixin from "VueMixins/ItemPublishSettingsMixin";
import ConfirmModal from "VueModals/ConfirmModal";
import TaggableModelMixin from 'VueMixins/TaggableModelMixin';

export default {
  components: {
    DisplayWithSelectionModal,
    ItemFeedsList,
    ItemStorageSelect,
    ChaptersTable,
    ConfirmModal,
  },
  mixins: [ItemPublishSettingsMixin, TaggableModelMixin],
  data() {
    return {
      timezone: window.user.timezone,
      new_storage_asset: null,
      storage_switcher_active: false,
      edit: false,
      loading_artwork: true,
      loading_storage: false,
      name: null,
      description: null,
      description_incl_chapters: true,
      tag: "",
      tags: [],
      selected_storage_item: null,
      storage_items: [],
      selected_artwork: null,
      downloadable: window.preferences.default_download,
      broadcast_start: null,
      broadcast_stop: null,
      return_url_str: ``,
      assetLoader: ldDebounce(this.getStorageItems, 250),
      upload_to_mixcloud: false,
    };
  },
  computed: {
    storage_file_url() {
      if (this.item.unpublished_storage_file_url) {
        return this.item.unpublished_storage_file_url;
      } else {
        return this.item.storage_file_url;
      }
    },
    artwork_to_display() {
      if (this.selected_artwork) {
        return this.selected_artwork;
      }

      if (this.item.artwork) {
        return this.item.artwork;
      }

      return null;
    },
    is_orphaned() {
      return this.edit && !this.item.storage;
    },
    show_storage_item_selection() {
      if (!this.edit) {
        return true;
      }

      return this.is_orphaned;
    },
  },
  watch: {
    item: function (newValue, oldValue) {
      if (newValue.uuid && !oldValue.uuid) {
        this.edit = true;
        this.prepareEdit();
      }
    },
    selected_storage_item: function (new_value) {
      EventBus.$emit('selected-storage-item', {
        selected_storage_item: new_value,
      });
    },
  },
  beforeMount() {
    if (this.item && this.item.uuid) {
      this.edit = true;
    }

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    this.unlink_from_mixcloud = urlParams.get('unlink_from_mixcloud') ?? false;
    this.upload_to_mixcloud = urlParams.get('upload_to_mixcloud') ?? false;
  },
  mounted() {
    this.fetchTags();
    this.getStorageItems();

    if (this.item && this.item.uuid) {
      this.edit = true;
      this.prepareEdit();
    }

    EventBus.$on("storage_switcher_cancelled", () => {
      this.storage_switcher_active = false;
    });

    this.return_url_str = [
      ...new URLSearchParams(window.location.search),
    ].map(([k, v]) => `${k}=${v}`).join(`&`);

    EventBus.$on('new_artwork_selected', (payload) => {
      toastr.success("New Artwork Selected. Don't forget to save the item.");
      this.selected_artwork = payload;
    });

    // from ItemsEdit
    EventBus.$on('item-save', () => {
      this.save();
    });
  },
  methods: {
    async confirmStorageAssetChange(new_asset) {
      const res = await this.fireModal(`confirm-storage-change`);
      if (res.data.confirm) {
        this.switchStorageAsset(new_asset);
      }
    },
    async switchStorageAsset(new_asset) {
      try {
        await axios.patch(`/api/v1/items/${this.item.uuid}/switch-storage`, {
          storage_uuid: new_asset.uuid,
        });
        toastr.success("Successfully switched storage asset. Reloading...");
        window.location.reload();
      } catch (error) {
        const { status, data } = error.response;
        if (status === 500 && data.success) { // if something peripheral fails it can return success: true with a bad status
          const timeout = 10000; // in ms
          toastr.warning("Successfully switched storage asset, but some errors occured!", null, {
            timeOut: timeout,
          });
          toastr.warning(error.message ?? 'Unknown error', null, {
            timeOut: timeout,
          });
          await new Promise(res => setTimeout(res, timeout)); // wait for the timeout before continuing
          window.location.reload();
        } else {
          this.handleAxiosError(error);
        }
      }
    },
    formatFileSize(size) {
      return formatBytes(size, 2);
    },
    prepareEdit() {
      this.ms_tag_values = this.item.tags;
      this.name = this.item.name;
      this.description = this.item.description;
      this.description_incl_chapters = this.item.description_incl_chapters;

      this.preparePublishSettings();

      if (Number(this.item.type) === 1) {
        this.broadcast_start = moment(mysqlDateTimeUTCToLocal(window.item.broadcast_start)).toISOString();
        this.broadcast_stop = moment(mysqlDateTimeUTCToLocal(window.item.broadcast_stop)).toISOString();
      }

      this.downloadable = this.item.downloadable;
    },
    async getStorageItems(name) {
      this.loading_storage = true;
      try {
        const filters = name ? `&filters=name:ct:${name}` : ``;
        const per_page = `per_page=${name ? 400 : 50}`;
        this.storage_items = (await axios.get(`/api/v1/storage?${per_page}${filters}`)).data.data;
      } catch (err) {
        this.storage_items = [];
        this.handleAxiosError(err);
      }
      this.loading_storage = false;
    },
    async save() {
      const artwork_uuid = this.selected_artwork?.uuid ?? this.item.artwork?.uuid ?? null;

      const data = {
        storage_uuid: this.selected_storage_item ? this.selected_storage_item.uuid : null,
        name: this.name,
        description: this.description,
        publish_start: this.schedule_publish_dates ? this.publish_start : null,
        publish_stop: this.schedule_publish_dates ? this.publish_stop : null,
        published: this.published,
        description_incl_chapters: this.description_incl_chapters,
        artwork_uuid: artwork_uuid,
        downloadable: this.downloadable,
        tags: this.ms_tag_values,
        upload_to_mixcloud: this.upload_to_mixcloud ?? false,
        unlink_from_mixcloud: this.unlink_from_mixcloud ?? false,
      };

      if (this.item && this.item.uuid) {
        data.uuid = this.item.uuid;
      }

      if (this.item.type === 1) {
        data.broadcast_start = this.broadcast_start;
        data.broadcast_stop = this.broadcast_stop;
      }

      try {
        const response = await axios.post("/api/v1/items", data);
        const response_item = response.data.data;
        // If dates are in range then publish toggle may have changed, so update it.
        this.published = response_item.published;

        if (!this.edit) {
          toastr.success("Item Saved. Redirecting...");
          window.location.href = `/items/${response_item.uuid}/edit`;
        } else {
          toastr.success("Item Saved");
          EventBus.$emit("item-updated", response.data.data);
          // window.location.href = `/items?${this.return_url_str}`;
        }
      } catch (error) {
        window.handleAxiosError(error);
      }
    },
  },
};

</script>
